import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Clock,
  Plus,
  DollarSign
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function ArtistTimeline() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [otEntries, setOtEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showOTDialog, setShowOTDialog] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [approvingPMs, setApprovingPMs] = useState([]);
  const [otForm, setOtForm] = useState({
    date: new Date().toISOString().split("T")[0],
    hours: 2,
    rate: "",
    program_id: "",
    approving_pm_id: "",
    notes: ""
  });
  const [submittingOT, setSubmittingOT] = useState(false);

  // Generate dates for current week
  const getWeekDates = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1); // Monday
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const weekDates = getWeekDates(currentDate);

  const fetchData = useCallback(async () => {
    try {
      const [assignRes, timeOffRes, holidayRes, otRes, programsRes, usersRes] = await Promise.all([
        apiClient.get("/artist/my-timeline"),
        apiClient.get("/artist/my-time-off"),
        apiClient.get("/holidays"),
        apiClient.get("/ot-entries/my-entries").catch(() => ({ data: [] })),
        apiClient.get("/programs?page_size=100").catch(() => ({ data: [] })),
        apiClient.get("/users").catch(() => ({ data: [] }))
      ]);
      setAssignments(assignRes.data || []);
      setTimeOffs(timeOffRes.data || []);
      setHolidays(holidayRes.data || []);
      setOtEntries(otRes.data || []);
      setPrograms(programsRes.data?.data || programsRes.data || []);
      // Filter for PMs who can approve OT
      const pms = (usersRes.data || []).filter(u => 
        ["COO", "Admin", "Program Manager", "Marketing PM"].includes(u.role)
      );
      setApprovingPMs(pms);
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  const getItemsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const items = [];
    
    // Check assignments
    assignments.forEach(a => {
      if (dateStr >= a.start_date && dateStr <= a.end_date) {
        items.push({ type: 'assignment', data: a });
      }
    });
    
    // Check time off
    timeOffs.forEach(t => {
      if (dateStr >= t.start_date && dateStr <= t.end_date) {
        items.push({ type: 'timeoff', data: t });
      }
    });
    
    // Check OT entries
    otEntries.forEach(ot => {
      if (ot.date === dateStr) {
        items.push({ type: 'ot', data: ot });
      }
    });
    
    // Check holidays
    holidays.forEach(h => {
      if (dateStr === h.date || (dateStr >= h.start_date && dateStr <= h.end_date)) {
        items.push({ type: 'holiday', data: h });
      }
    });
    
    return items;
  };

  const handleSubmitOT = async () => {
    if (!otForm.hours || !otForm.approving_pm_id) {
      toast.error("Please fill in hours and approving PM");
      return;
    }
    
    setSubmittingOT(true);
    try {
      await apiClient.post("/ot-entries", {
        artist_id: user?.resource_id || user?.id,
        date: otForm.date,
        hours: parseFloat(otForm.hours),
        rate_override: otForm.rate ? parseFloat(otForm.rate) : null,
        program_id: otForm.program_id || null,
        approving_pm_id: otForm.approving_pm_id,
        notes: otForm.notes
      });
      toast.success("OT entry submitted for approval");
      setShowOTDialog(false);
      setOtForm({
        date: new Date().toISOString().split("T")[0],
        hours: 2,
        rate: "",
        program_id: "",
        approving_pm_id: "",
        notes: ""
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit OT");
    } finally {
      setSubmittingOT(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artist-timeline">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
          My Timeline
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[180px] text-center">
            {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
          </span>
          <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowOTDialog(true)}
            className="bg-amber-700 hover:bg-amber-600 text-white"
            data-testid="log-ot-btn"
          >
            <Clock className="w-4 h-4 mr-1" />
            Log OT
          </Button>
        </div>
      </div>

      {/* Week View */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-7 border-b">
            {weekDates.map((date, idx) => (
              <div 
                key={idx} 
                className={`p-3 text-center border-r last:border-r-0 ${isWeekend(date) ? 'bg-gray-50' : ''}`}
              >
                <p className="text-xs text-muted-foreground">{date.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                <p className="text-lg font-semibold">{date.getDate()}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 min-h-[300px]">
            {weekDates.map((date, idx) => {
              const items = getItemsForDate(date);
              return (
                <div 
                  key={idx} 
                  className={`p-2 border-r last:border-r-0 ${isWeekend(date) ? 'bg-gray-50/50' : ''}`}
                >
                  {items.map((item, i) => (
                    <div 
                      key={i}
                      className={`p-2 mb-1 rounded text-xs ${
                        item.type === 'assignment' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'timeoff' ? 'bg-orange-100 text-orange-800' :
                        item.type === 'ot' ? 'bg-green-100 text-green-800 border border-green-300' :
                        'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {item.type === 'assignment' && item.data.program_name}
                      {item.type === 'timeoff' && `🏖️ ${item.data.leave_type}`}
                      {item.type === 'holiday' && `🎉 ${item.data.name}`}
                      {item.type === 'ot' && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{item.data.hours}h OT</span>
                          {item.data.status === 'approved' && <span className="text-green-600">✓</span>}
                          {item.data.status === 'pending' && <span className="text-yellow-600">⏳</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
          <span>Assignment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
          <span>Time Off</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
          <span>Overtime</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" />
          <span>Holiday</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
          <span>Weekend</span>
        </div>
      </div>

      {/* OT Log Dialog */}
      <Dialog open={showOTDialog} onOpenChange={setShowOTDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Log Overtime
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={otForm.date}
                  onChange={(e) => setOtForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Hours *</Label>
                <Input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={otForm.hours}
                  onChange={(e) => setOtForm(prev => ({ ...prev, hours: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Rate Override (optional)</Label>
              <Input
                type="number"
                placeholder="Default rate will be used if empty"
                value={otForm.rate}
                onChange={(e) => setOtForm(prev => ({ ...prev, rate: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Project (optional)</Label>
              <Select
                value={otForm.program_id}
                onValueChange={(value) => setOtForm(prev => ({ ...prev, program_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No specific project</SelectItem>
                  {[...programs].sort((a, b) => (a.program_name || "").localeCompare(b.program_name || "")).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Approving PM *</Label>
              <Select
                value={otForm.approving_pm_id}
                onValueChange={(value) => setOtForm(prev => ({ ...prev, approving_pm_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select PM" />
                </SelectTrigger>
                <SelectContent>
                  {approvingPMs.map(pm => (
                    <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                value={otForm.notes}
                onChange={(e) => setOtForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Reason for overtime"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOTDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitOT}
              disabled={submittingOT}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {submittingOT ? "Submitting..." : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
